import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Eye, LogOut, Download, Loader2, Mail, Send, ImageIcon, Sparkles, Layers, FileImage, BookOpen, RefreshCw, X, Edit3 } from 'lucide-react';
import MagnetEditor from '../../components/MagnetEditor';
import SubscriberProgress from '../../components/SubscriberProgress';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isImporting, setIsImporting] = useState(false);
  const [digestEmail, setDigestEmail] = useState('');
  const [isSendingDigest, setIsSendingDigest] = useState(false);
  const [isRebalancing, setIsRebalancing] = useState(false);
  const [isBackfilling, setIsBackfilling] = useState(false);
  const [seoStatus, setSeoStatus] = useState(null);
  const [isGeneratingOG, setIsGeneratingOG] = useState(false);
  const [ogStatus, setOgStatus] = useState(null);
  const [isGeneratingCarousel, setIsGeneratingCarousel] = useState(false);
  const [magnets, setMagnets] = useState([]);
  const [isMagnetActing, setIsMagnetActing] = useState('');  // slug being acted on
  const [customTopicOpen, setCustomTopicOpen] = useState(false);
  const [customTopic, setCustomTopic] = useState('');
  const [isCreatingCustom, setIsCreatingCustom] = useState(false);
  const [editingMagnetSlug, setEditingMagnetSlug] = useState('');
  const [refreshAllStatus, setRefreshAllStatus] = useState(null);
  const [isRefreshingAll, setIsRefreshingAll] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchPosts();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return null;
    }
    return { Authorization: `Bearer ${token}` };
  };

  const fetchPosts = async () => {
    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      const response = await axios.get(`${BACKEND_URL}/api/admin/posts`, { headers });
      setPosts(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        navigate('/admin/login');
      } else {
        toast.error('Failed to fetch posts');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (postId, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"?`)) {
      return;
    }

    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      await axios.delete(`${BACKEND_URL}/api/admin/posts/${postId}`, { headers });
      toast.success('Post deleted successfully');
      fetchPosts();
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  const handleRebalanceImages = async () => {
    if (!window.confirm('Re-assign unique cover images to every post? Custom uploads will be preserved.')) return;
    const headers = getAuthHeaders();
    if (!headers) return;
    setIsRebalancing(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/rebalance-images`, {}, { headers });
      const { updated, preserved_custom_uploads, total_posts } = res.data;
      toast.success(`Refreshed ${updated} of ${total_posts} covers (${preserved_custom_uploads} custom uploads preserved)`);
      fetchPosts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to rebalance images');
    } finally {
      setIsRebalancing(false);
    }
  };

  const fetchSeoStatus = async () => {
    const headers = getAuthHeaders();
    if (!headers) return;
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/seo/status`, { headers });
      setSeoStatus(res.data);
    } catch { /* silent */ }
  };

  useEffect(() => { fetchSeoStatus(); fetchOgStatus(); fetchMagnets(); }, []);

  const fetchMagnets = async () => {
    const headers = getAuthHeaders();
    if (!headers) return;
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/magnets`, { headers });
      setMagnets(res.data.magnets || []);
    } catch { /* silent */ }
  };

  const handleMagnetRegenerate = async (slug) => {
    const headers = getAuthHeaders();
    if (!headers) return;
    setIsMagnetActing(slug);
    const toastId = toast.loading('Generating guide…');
    try {
      await axios.post(`${BACKEND_URL}/api/admin/magnets/${slug}/regenerate`, {}, { headers, timeout: 180000 });
      toast.success('Guide refreshed', { id: toastId });
      fetchMagnets();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to generate', { id: toastId });
    } finally {
      setIsMagnetActing('');
    }
  };

  const handleMagnetDelete = async (slug) => {
    if (!window.confirm(`Delete the custom guide "${slug}"? This cannot be undone.`)) return;
    const headers = getAuthHeaders();
    if (!headers) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/magnets/${slug}`, { headers });
      toast.success('Custom guide deleted');
      fetchMagnets();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const handleRefreshAllMagnets = async () => {
    if (!window.confirm('Regenerate every Free Guide via LLM? This runs in the background and refreshes all guides on the catalog.')) return;
    const headers = getAuthHeaders();
    if (!headers) return;
    setIsRefreshingAll(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/magnets/refresh-all`, {}, { headers });
      toast.success('Refresh started in background. Polling for progress…');
      pollRefreshAllStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to start refresh');
      setIsRefreshingAll(false);
    }
  };

  const pollRefreshAllStatus = async () => {
    const headers = getAuthHeaders();
    if (!headers) return;
    const tick = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/admin/magnets/refresh-all/status`, { headers });
        setRefreshAllStatus(res.data);
        if (res.data.running) {
          setTimeout(tick, 4000);
        } else {
          setIsRefreshingAll(false);
          if (res.data.total > 0 && res.data.finished_at) {
            toast.success(`Refresh complete — ${res.data.completed}/${res.data.total} guides updated${res.data.failed ? ` (${res.data.failed} failed)` : ''}`);
            fetchMagnets();
          }
        }
      } catch {
        setIsRefreshingAll(false);
      }
    };
    tick();
  };

  const handleCreateCustom = async () => {
    const topic = customTopic.trim();
    if (topic.length < 4) { toast.error('Give me a topic (at least 4 characters)'); return; }
    const headers = getAuthHeaders();
    if (!headers) return;
    setIsCreatingCustom(true);
    const toastId = toast.loading('Crafting your custom guide… up to 30s');
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/magnets/custom`, { topic }, { headers, timeout: 180000 });
      toast.success(`Created: ${res.data.magnet.title}`, { id: toastId });
      setCustomTopic('');
      setCustomTopicOpen(false);
      fetchMagnets();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Custom guide failed', { id: toastId });
    } finally {
      setIsCreatingCustom(false);
    }
  };

  const fetchOgStatus = async () => {
    const headers = getAuthHeaders();
    if (!headers) return;
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/og-images/status`, { headers });
      setOgStatus(res.data);
    } catch { /* silent */ }
  };

  const handleGenerateOG = async () => {
    if (!window.confirm('Generate cover images for all pending posts? Each costs ~1 Nano Banana call (~$0.02).')) return;
    const headers = getAuthHeaders();
    if (!headers) return;
    setIsGeneratingOG(true);
    const toastId = toast.loading('Generating cover images via Nano Banana…');
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/og-images/generate`, {}, { headers, timeout: 600000 });
      const { updated, considered, failed } = res.data;
      toast.success(`OG images: ${updated}/${considered} generated${failed ? ` (${failed} failed)` : ''}`, { id: toastId, duration: 8000 });
      fetchOgStatus();
      fetchPosts();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'OG generation failed', { id: toastId });
    } finally {
      setIsGeneratingOG(false);
    }
  };

  const handleGenerateCarousel = async () => {
    const headers = getAuthHeaders();
    if (!headers) return;
    setIsGeneratingCarousel(true);
    const toastId = toast.loading('Generating LinkedIn carousel PDF…');
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/carousel/generate`,
        {},
        { headers, responseType: 'blob', timeout: 120000 },
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `linkedin-carousel-${new Date().toISOString().slice(0, 10)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Carousel PDF downloaded — drop it into LinkedIn as a document post.', { id: toastId, duration: 8000 });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Carousel generation failed', { id: toastId });
    } finally {
      setIsGeneratingCarousel(false);
    }
  };

  const handleSeoBackfill = async (force = false) => {
    const pending = (seoStatus?.posts?.pending || 0) + (seoStatus?.lessons?.pending || 0);
    const target = force ? 'every post and lesson' : `${pending} item${pending === 1 ? '' : 's'}`;
    if (!window.confirm(`Run SEO backfill on ${target}? Each item costs ~1 LLM call (gpt-4o-mini).`)) return;
    const headers = getAuthHeaders();
    if (!headers) return;
    setIsBackfilling(true);
    const toastId = toast.loading('Backfilling TL;DR + meta + keywords… this can take a minute');
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/seo/backfill`,
        { force },
        { headers, timeout: 300000 },
      );
      const { posts, lessons } = res.data;
      toast.success(
        `SEO backfill complete — posts ${posts.updated}/${posts.considered}, lessons ${lessons.updated}/${lessons.considered}`,
        { id: toastId, duration: 8000 },
      );
      fetchSeoStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'SEO backfill failed', { id: toastId });
    } finally {
      setIsBackfilling(false);
    }
  };

  const handleImportNews = async () => {
    setIsImporting(true);
    try {
      const headers = getAuthHeaders();
      if (!headers) return;

      toast.info('Searching for latest AI news and generating blog post...');
      
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/import-news`,
        {
          topics: ["AI ethics", "ethical AI", "teens and AI", "AI safety", "responsible AI"],
          count: 1
        },
        { headers, timeout: 120000 } // 2 minute timeout for LLM processing
      );

      toast.success(`Imported: ${response.data.titles.join(', ')}`);
      fetchPosts(); // Refresh the posts list
    } catch (error) {
      console.error('Import error:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.');
        navigate('/admin/login');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to import news. Please try again.');
      }
    } finally {
      setIsImporting(false);
    }
  };

  const sendDigest = async (testEmail) => {
    setIsSendingDigest(true);
    try {
      const headers = getAuthHeaders();
      if (!headers) return;
      const body = testEmail ? { test_email: testEmail } : {};
      const res = await axios.post(`${BACKEND_URL}/api/admin/digest/send`, body, {
        headers, timeout: 120000,
      });
      const { sent, recipients, failed, posts_count, lessons_count, failures } = res.data;
      if (sent > 0 && !failed) {
        toast.success(
          `Digest sent to ${sent}/${recipients} (${posts_count} posts, ${lessons_count} lessons)`,
          { duration: 5000 }
        );
      } else {
        // Include the actual Resend error in the toast so we know what's wrong.
        const firstErr = (failures && failures[0]?.error) || 'Unknown error';
        toast.error(
          `Digest failed ${failed}/${recipients} — ${firstErr}`,
          { duration: 12000, description: failures && failures.length > 1 ? `+${failures.length - 1} more` : undefined }
        );
        console.error('Digest failures:', failures);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Digest send failed');
    } finally {
      setIsSendingDigest(false);
    }
  };

  const handleCheckEmailConfig = async () => {
    const headers = getAuthHeaders();
    if (!headers) return;
    try {
      const [cfg, test] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/digest/config`, { headers }),
        axios.post(`${BACKEND_URL}/api/admin/digest/test`, {}, { headers, timeout: 30000 }),
      ]);
      const c = cfg.data;
      const t = test.data;
      const lines = [
        `RESEND_API_KEY: ${c.resend_api_key_present ? '✓ ' + c.resend_api_key : '✗ MISSING'}`,
        `SENDER_EMAIL:   ${c.sender_email}`,
        `PUBLIC_SITE_URL: ${c.public_site_url}`,
        `Test send to ${t.target}: ${t.ok ? '✓ ok (id=' + t.id + ')' : '✗ ' + t.error}`,
      ];
      const report = lines.join('\n');
      console.log('[Email Config]\n' + report);
      if (t.ok && c.resend_api_key_present) {
        toast.success('Email config OK — see console for full report', { duration: 8000, description: 'Resend test send succeeded' });
      } else {
        toast.error('Email config issue detected', { duration: 20000, description: report });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Config check failed');
    }
  };

  const handleSendTestDigest = () => {
    const email = (digestEmail || '').trim();
    if (!email) { toast.error('Enter an email address first'); return; }
    sendDigest(email);
  };

  const handleSendToAll = () => {
    if (!window.confirm('Send the daily digest to ALL active subscribers right now?')) return;
    sendDigest(null);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-light">Admin Dashboard</h1>
          <div className="flex items-center space-x-4">
            <Link to="/" className="text-sm text-gray-600 hover:text-black">
              View Site
            </Link>
            <Link to="/admin/lessons" className="text-sm text-gray-600 hover:text-black" data-testid="admin-lessons-link">
              Lessons
            </Link>
            <Link to="/admin/social" className="text-sm text-gray-600 hover:text-black" data-testid="admin-social-link">
              Social
            </Link>
            <Link to="/admin/subscribers" className="text-sm text-gray-600 hover:text-black" data-testid="admin-subscribers-link">
              Subscribers
            </Link>
            <Link to="/admin/change-password" className="text-sm text-gray-600 hover:text-black">
              Change Password
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 text-sm text-gray-600 hover:text-black"
            >
              <LogOut size={16} />
              <span>Logout</span>
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Subscriber growth progress (admin-only) */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-8" data-testid="admin-subscriber-progress-panel">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-medium">Subscriber growth</h3>
              <p className="text-sm text-gray-600 mt-1">Progress toward the 10,000-reader goal. Visible to admins only.</p>
            </div>
          </div>
          <SubscriberProgress />
        </div>

        {/* Distribution toolkit (OG images + LinkedIn carousel) */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div className="bg-white border border-gray-200 rounded-lg p-6" data-testid="og-panel">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-violet-50 flex items-center justify-center flex-shrink-0">
                <FileImage size={20} className="text-violet-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-medium">Cover Images (OG)</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Custom AI-generated covers for social-share previews via Nano Banana.
                </p>
                {ogStatus ? (
                  <p className="text-xs text-gray-500 mt-2" data-testid="og-status">
                    <span className="font-semibold text-gray-700">{ogStatus.with_og_image}/{ogStatus.total}</span> covers
                    {ogStatus.pending > 0 ? <span className="text-amber-700"> · {ogStatus.pending} pending</span> : null}
                  </p>
                ) : null}
              </div>
              <button
                onClick={handleGenerateOG}
                disabled={isGeneratingOG}
                className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-md hover:bg-violet-700 disabled:opacity-50 text-sm"
                data-testid="og-generate-btn"
              >
                {isGeneratingOG ? <Loader2 size={16} className="animate-spin" /> : <FileImage size={16} />}
                <span>{isGeneratingOG ? 'Generating…' : 'Generate covers'}</span>
              </button>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-6" data-testid="carousel-panel">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-cyan-50 flex items-center justify-center flex-shrink-0">
                <Layers size={20} className="text-cyan-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-medium">LinkedIn Carousel</h3>
                <p className="text-sm text-gray-600 mt-1">
                  Turns your latest post into an 8-slide branded PDF for LinkedIn document posts (5-10× reach vs text).
                </p>
              </div>
              <button
                onClick={handleGenerateCarousel}
                disabled={isGeneratingCarousel}
                className="flex items-center gap-2 px-4 py-2 bg-black text-white rounded-md hover:bg-gray-800 disabled:opacity-50 text-sm"
                data-testid="carousel-generate-btn"
              >
                {isGeneratingCarousel ? <Loader2 size={16} className="animate-spin" /> : <Layers size={16} />}
                <span>{isGeneratingCarousel ? 'Generating…' : 'Generate PDF'}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Free Guides (PDF magnets) panel */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-8" data-testid="magnets-panel">
          <div className="flex items-start gap-3 mb-5">
            <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center flex-shrink-0">
              <BookOpen size={20} className="text-amber-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-medium">Free Guides (PDF magnets)</h3>
              <p className="text-sm text-gray-600 mt-1">
                Generate or refresh the downloadable guides shown at <a href="/guides" target="_blank" rel="noreferrer" className="underline">/guides</a> and unlocked via <a href="/guides-pdf" target="_blank" rel="noreferrer" className="underline">/guides-pdf</a>. Each guide costs ~1 LLM call.
              </p>
              <p className="text-xs text-gray-500 mt-2">
                <span className="font-semibold text-gray-700">{magnets.filter(m => m.is_generated).length}/{magnets.length}</span> generated
                <span className="mx-2">·</span>
                {magnets.filter(m => m.is_custom).length} custom
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleRefreshAllMagnets}
                disabled={isRefreshingAll}
                className="flex items-center gap-2 px-3 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-50 text-sm"
                data-testid="magnet-refresh-all-btn"
                title="Regenerate every guide via LLM"
              >
                {isRefreshingAll ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                <span>{isRefreshingAll ? 'Refreshing…' : 'Refresh all'}</span>
              </button>
              <button
                onClick={() => setCustomTopicOpen((v) => !v)}
                className="flex items-center gap-2 px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm"
                data-testid="magnet-custom-toggle"
              >
                <Plus size={14} /> <span>Custom topic</span>
              </button>
            </div>
          </div>

          {isRefreshingAll || (refreshAllStatus && refreshAllStatus.total > 0 && refreshAllStatus.finished_at) ? (
            <div className="mb-5 p-3 bg-amber-50 border border-amber-200 rounded-md text-sm" data-testid="magnet-refresh-progress">
              <div className="flex items-center justify-between">
                <span className="font-medium text-amber-900">
                  {refreshAllStatus?.running ? 'Refreshing guides…' : 'Last refresh complete'}
                </span>
                <span className="tabular-nums text-amber-800">
                  {refreshAllStatus?.completed ?? 0} / {refreshAllStatus?.total ?? 0}
                  {refreshAllStatus?.failed ? <span className="text-red-700 ml-2">· {refreshAllStatus.failed} failed</span> : null}
                </span>
              </div>
              {refreshAllStatus?.last_slug && refreshAllStatus?.running ? (
                <p className="text-xs text-amber-700 mt-1">Currently: <code>{refreshAllStatus.last_slug}</code></p>
              ) : null}
              <div className="mt-2 h-1.5 bg-amber-100 rounded overflow-hidden">
                <div
                  className="h-full bg-amber-600 transition-all"
                  style={{ width: `${refreshAllStatus?.total ? Math.round(100 * (refreshAllStatus.completed / refreshAllStatus.total)) : 0}%` }}
                />
              </div>
            </div>
          ) : null}

          {customTopicOpen ? (
            <div className="mb-5 p-4 bg-amber-50/40 border border-amber-200 rounded-lg" data-testid="magnet-custom-form">
              <label className="block text-xs font-medium text-amber-900 mb-2 uppercase tracking-wider">
                What's the topic? (e.g. "AI agents for high-school robotics", "Vibe coding with Lovable + v0")
              </label>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  value={customTopic}
                  onChange={(e) => setCustomTopic(e.target.value)}
                  placeholder="Describe the guide you want generated…"
                  className="flex-1 px-3 py-2 border border-amber-300 bg-white rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                  data-testid="magnet-custom-input"
                  onKeyDown={(e) => { if (e.key === 'Enter') handleCreateCustom(); }}
                />
                <button
                  onClick={handleCreateCustom}
                  disabled={isCreatingCustom}
                  className="px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-50 text-sm inline-flex items-center justify-center gap-2"
                  data-testid="magnet-custom-create-btn"
                >
                  {isCreatingCustom ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                  <span>{isCreatingCustom ? 'Creating…' : 'Create guide'}</span>
                </button>
                <button
                  onClick={() => { setCustomTopicOpen(false); setCustomTopic(''); }}
                  className="px-3 py-2 text-gray-500 hover:text-black text-sm"
                  data-testid="magnet-custom-cancel"
                >
                  <X size={14} />
                </button>
              </div>
              <p className="text-xs text-amber-700 mt-2">The LLM will pick a title, subtitle, slug, and write the full markdown body — appears immediately on /guides.</p>
            </div>
          ) : null}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="magnet-list">
            {magnets.map((m) => (
              <div
                key={m.slug}
                className="flex items-start gap-3 p-3 border border-gray-200 rounded-md hover:border-gray-400 transition"
                data-testid={`magnet-row-${m.slug}`}
              >
                <div className={`w-10 h-10 rounded-md bg-gradient-to-br ${m.color || 'from-slate-500 to-zinc-500'} flex-shrink-0`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-medium text-sm text-gray-900 truncate">{m.title}</h4>
                    {m.is_custom ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-100 text-purple-800 font-semibold uppercase tracking-wider">custom</span> : null}
                    {!m.is_generated ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 font-semibold uppercase tracking-wider">pending</span> : null}
                  </div>
                  <p className="text-xs text-gray-600 line-clamp-2">{m.subtitle}</p>
                  {m.generated_at ? <p className="text-[10px] text-gray-400 mt-1">Updated {new Date(m.generated_at).toLocaleDateString()}</p> : null}
                </div>
                <div className="flex flex-col gap-1 flex-shrink-0">
                  <button
                    onClick={() => handleMagnetRegenerate(m.slug)}
                    disabled={isMagnetActing === m.slug}
                    className="text-xs px-3 py-1.5 bg-black text-white rounded hover:bg-gray-800 disabled:opacity-40 inline-flex items-center gap-1"
                    data-testid={`magnet-regen-${m.slug}`}
                    title={m.is_generated ? 'Regenerate with LLM' : 'Generate with LLM'}
                  >
                    {isMagnetActing === m.slug ? <Loader2 size={12} className="animate-spin" /> : (m.is_generated ? <RefreshCw size={12} /> : <Sparkles size={12} />)}
                    <span>{m.is_generated ? 'Refresh' : 'Generate'}</span>
                  </button>
                  {m.is_generated ? (
                    <button
                      onClick={() => setEditingMagnetSlug(m.slug)}
                      className="text-xs px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50 inline-flex items-center gap-1"
                      data-testid={`magnet-edit-${m.slug}`}
                      title="Edit body inline"
                    >
                      <Edit3 size={12} /> Edit
                    </button>
                  ) : null}
                  {m.is_custom ? (
                    <button
                      onClick={() => handleMagnetDelete(m.slug)}
                      className="text-xs px-3 py-1.5 text-red-600 hover:bg-red-50 rounded inline-flex items-center gap-1"
                      data-testid={`magnet-delete-${m.slug}`}
                    >
                      <Trash2 size={12} /> Delete
                    </button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* SEO Backfill Panel */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-8" data-testid="seo-panel">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center flex-shrink-0">
              <Sparkles size={20} className="text-amber-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-medium">SEO Backfill</h3>
              <p className="text-sm text-gray-600 mt-1">
                Generates a TL;DR, meta description, and keywords for older posts
                and lessons that pre-date the SEO-aware prompts.
              </p>
              {seoStatus ? (
                <p className="text-xs text-gray-500 mt-2" data-testid="seo-status">
                  Posts: <span className="font-semibold text-gray-700">{seoStatus.posts.with_seo}/{seoStatus.posts.total}</span> done
                  {seoStatus.posts.pending > 0 ? <span className="text-amber-700"> · {seoStatus.posts.pending} pending</span> : null}
                  <span className="mx-2">·</span>
                  Lessons: <span className="font-semibold text-gray-700">{seoStatus.lessons.with_seo}/{seoStatus.lessons.total}</span> done
                  {seoStatus.lessons.pending > 0 ? <span className="text-amber-700"> · {seoStatus.lessons.pending} pending</span> : null}
                </p>
              ) : null}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handleSeoBackfill(false)}
                disabled={isBackfilling}
                className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-50 text-sm"
                data-testid="seo-backfill-btn"
              >
                {isBackfilling ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                <span>{isBackfilling ? 'Running…' : 'Backfill missing'}</span>
              </button>
              <button
                onClick={() => handleSeoBackfill(true)}
                disabled={isBackfilling}
                className="text-xs text-gray-500 hover:text-black px-2"
                data-testid="seo-backfill-force-btn"
                title="Regenerate even on items that already have SEO fields"
              >
                Force all
              </button>
            </div>
          </div>
        </div>

        {/* Daily Digest Panel */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-8" data-testid="digest-panel">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center flex-shrink-0">
              <Mail size={20} className="text-indigo-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-medium">Daily Digest Email</h3>
              <p className="text-sm text-gray-600 mt-1">
                Sends new articles + lessons from the last 24 hours to subscribers.
                Auto-runs every day at 09:00 UTC.
              </p>
            </div>
            <button
              onClick={handleCheckEmailConfig}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-600 hover:text-black border border-gray-300 rounded-md hover:bg-gray-50"
              data-testid="email-config-check-btn"
              title="Verify Resend API key, sender address, and do a diagnostic send"
            >
              <Mail size={13} />
              <span>Check Email Config</span>
            </button>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 mt-4">
            <input
              type="email"
              placeholder="test@example.com"
              value={digestEmail}
              onChange={(e) => setDigestEmail(e.target.value)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-black focus:border-transparent text-sm"
              data-testid="digest-test-email-input"
            />
            <button
              onClick={handleSendTestDigest}
              disabled={isSendingDigest}
              className="flex items-center justify-center gap-2 px-5 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-50 text-sm"
              data-testid="digest-send-test-btn"
            >
              {isSendingDigest ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
              <span>Send Sample</span>
            </button>
            <button
              onClick={handleSendToAll}
              disabled={isSendingDigest}
              className="flex items-center justify-center gap-2 px-5 py-2 bg-black text-white rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50 text-sm"
              data-testid="digest-send-all-btn"
            >
              <Mail size={16} />
              <span>Send to All Subscribers</span>
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-between items-center mb-8">
          <h2 className="text-3xl font-light">Blog Posts</h2>
          <div className="flex items-center space-x-4">
            <button
              onClick={handleRebalanceImages}
              disabled={isRebalancing}
              className="flex items-center space-x-2 px-4 py-3 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
              data-testid="rebalance-images-btn"
              title="Replace any duplicate cover images with unique ones"
            >
              {isRebalancing ? (
                <Loader2 size={18} className="animate-spin" />
              ) : (
                <ImageIcon size={18} />
              )}
              <span>{isRebalancing ? 'Refreshing…' : 'Refresh Covers'}</span>
            </button>
            <button
              onClick={handleImportNews}
              disabled={isImporting}
              className="flex items-center space-x-2 px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="import-news-btn"
            >
              {isImporting ? (
                <>
                  <Loader2 size={20} className="animate-spin" />
                  <span>Importing...</span>
                </>
              ) : (
                <>
                  <Download size={20} />
                  <span>Import AI News</span>
                </>
              )}
            </button>
            <Link
              to="/admin/post/new"
              className="flex items-center space-x-2 px-6 py-3 bg-black text-white rounded-md hover:bg-gray-800 transition-colors"
            >
              <Plus size={20} />
              <span>New Post</span>
            </Link>
          </div>
        </div>

        {/* Posts Table */}
        {posts.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
            <p className="text-gray-600 mb-4">No posts yet</p>
            <Link
              to="/admin/post/new"
              className="inline-flex items-center space-x-2 px-6 py-3 bg-black text-white rounded-md hover:bg-gray-800 transition-colors"
            >
              <Plus size={20} />
              <span>Create Your First Post</span>
            </Link>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Title</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Category</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Date</th>
                  <th className="px-6 py-4 text-left text-sm font-medium text-gray-600">Status</th>
                  <th className="px-6 py-4 text-right text-sm font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {posts.map((post) => (
                  <tr key={post.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium">{post.title}</div>
                      <div className="text-sm text-gray-500">{post.excerpt.slice(0, 60)}...</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{post.category}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">
                      {new Date(post.date).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`px-3 py-1 text-xs rounded-full ${
                          post.published
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {post.published ? 'Published' : 'Draft'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end space-x-2">
                        <Link
                          to={`/post/${post.slug}`}
                          className="p-2 text-gray-600 hover:text-black"
                          title="View"
                        >
                          <Eye size={18} />
                        </Link>
                        <Link
                          to={`/admin/post/${post.id}`}
                          className="p-2 text-gray-600 hover:text-black"
                          title="Edit"
                        >
                          <Edit size={18} />
                        </Link>
                        <button
                          onClick={() => handleDelete(post.id, post.title)}
                          className="p-2 text-gray-600 hover:text-red-600"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* Magnet inline editor (modal) */}
      {editingMagnetSlug ? (
        <MagnetEditor
          slug={editingMagnetSlug}
          onClose={() => setEditingMagnetSlug('')}
          onSaved={fetchMagnets}
          getAuthHeaders={getAuthHeaders}
        />
      ) : null}
    </div>
  );
};

export default AdminDashboard;
